import CustomerBankAccountDTO from "features/customer/dtos/CustomerBankAccountDTO";
import PageCard from "features/shared/components/card/PageCard";
import PaymentMethodTypeEnumDTO from "features/shared/enums/dynamic/PaymentMethodTypeEnumDTO";
import FormGroup from "features/shared/forms/components/FormGroup";
import React from "react";
import Select from "react-select";
import reactSelectStyles from 'features/shared/components/reactSelect/reactselectcustom.module.scss';
import { Nullable } from "features/shared/types/nullable";
import PaymentMethodCategoryDTO from "features/payments/dtos/PaymentMethodCategoryDTO";
import { FormFields } from "features/shared/forms/hooks/useForm";
import BankAccountSelect from 'features/requirements/components/paymentMethodForm/BankAccountSelect';
import SipoSelect from 'features/requirements/components/paymentMethodForm/SipoSelect';
import CustomerSipoAccountDTO from "features/customer/dtos/CustomerSipoAccountDTO";
import { paymentCategoriesMapping } from "features/requirements/sections/requirementFormTypes/paymentMethodForm/paymentCategoriesMapping";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IPaymentMethodFormService from "features/requirements/services/IPaymentMethodFormService";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { PaymentMethodFormSection_PageCard } from "features/shared/components/loaders/promiseLoadingAreas";
import { useTranslation } from "react-i18next";

interface Props {
  paymentMethodType: PaymentMethodTypeEnumDTO;
  bankAccounts: CustomerBankAccountDTO[];
  sipos: CustomerSipoAccountDTO[];
  values: FormFields;
  errors: Record<string, string>;
  onPaymentMethodChange: (category: Nullable<PaymentMethodCategoryDTO>, paymentMethodType: string) => void;
  onBankAccountChange: (bankAccount: Nullable<CustomerBankAccountDTO>, paymentMethodType: string) => void;
  onSipoChange: (sipo: Nullable<CustomerSipoAccountDTO>, paymentMethodType: string) => void;
  onBankAccountAddClick: (paymentCategory?: string) => void;
  onSipoAccountAddClick: (paymentCategory?: string) => void;
}

const PaymentMethodSubForm = ({
  paymentMethodType,
  bankAccounts,
  sipos,
  values,
  errors,
  onPaymentMethodChange,
  onBankAccountChange,
  onSipoChange,
  onBankAccountAddClick,
  onSipoAccountAddClick
}: Props) => {
  //#region translations
  const tLabels = useTranslation("requirements\\requirements").t;
  //#endregion translations

  //#region  DI
  const formService = useInjection<IPaymentMethodFormService>(IocTypes.PaymentMethodFormService);
  //#endregion DI

  //#region private props
  const PaymentMethodInputName = `${paymentCategoriesMapping[paymentMethodType.code!]}Zpusob`;
  const BankAccountInputName = `${paymentCategoriesMapping[paymentMethodType.code!]}BankUcet`;
  const SipoAccountInputName = `${paymentCategoriesMapping[paymentMethodType.code!]}CisloSipo`;
  //#endregion  private props

  //#region helpers 
  const findSelectedValue = <T extends object | string>(data: T[], inputName: string, searchProperty?: string): Nullable<T> => 
    formService.findSelectedValue(data, values, inputName, searchProperty);
  //#endregion helpers

  //#region handlers
  const bankAccountChangeHandler = (bankAccount: Nullable<CustomerBankAccountDTO>) => {
    onBankAccountChange(bankAccount, paymentMethodType.code!);
  }

  const sipoChangeHandler = (sipo: Nullable<CustomerSipoAccountDTO>) => {
    onSipoChange(sipo, paymentMethodType.code!);
  }
  //#endregion handlers

  //#region sub-renders
  const renderAccount = () => {
    let selectedPaymentMethod = findSelectedValue<PaymentMethodCategoryDTO>(
      paymentMethodType.categories!,
      PaymentMethodInputName,
      "paymentMethodTypeId"
    )

    if (selectedPaymentMethod) {
      switch (selectedPaymentMethod.accountType) {
        case "BankAccount":
        case "IBAN":
          return (
            <BankAccountSelect
              name={BankAccountInputName}
              accounts={bankAccounts}
              onChange={bankAccountChangeHandler}
              selectedAccount={formService.findSelectedBankAccount(bankAccounts, values, paymentMethodType.code!)}
              onBankAccountAddClick={onBankAccountAddClick}
              errors={errors}
              paymentCategory={paymentMethodType.code}
            />
          )
        case "Sipo":
          return (
            <SipoSelect
              name={SipoAccountInputName}
              sipos={sipos}
              onChange={sipoChangeHandler}
              selectedSipo={formService.findSelectedSipo(sipos, values, paymentMethodType.code!)}
              onSipoAccountAddClick={onSipoAccountAddClick}
              errors={errors}
              paymentCategory={paymentMethodType.code}
            />
          )
        default:
          return null;
      }
    }
  }  
  //#endregion sub-renders

  //#region render
  return (
    <PageCard 
      title={paymentMethodType.name!} 
      bodyClassName="pt-4 px-4"
      loadingAreaName={PaymentMethodFormSection_PageCard}
      loadingDelay={0}
    >
      <div className="row no-gutters mx-n2">
        <div className="col-sm-6 col-xl-12 px-2">
          <FormGroup 
            labelText={tLabels("paymentMethodSubForm.paymentMethod")}
            validationError={errors[PaymentMethodInputName] ?? undefined}
          >
            <Select
              options={paymentMethodType.categories}
              getOptionLabel={(methodCategory) => methodCategory.name!}
              getOptionValue={(methodCategory) => methodCategory.code!}
              value={findSelectedValue<PaymentMethodCategoryDTO>(
                paymentMethodType.categories!,
                PaymentMethodInputName,
                "paymentMethodTypeId"
              )}
              onChange={(val) => onPaymentMethodChange(val, paymentMethodType.code!)}
              isSearchable={false}
              captureMenuScroll={false}
              className={reactSelectStyles.customSelect}
              classNamePrefix="customSelect"
              placeholder={tLabels("paymentMethodSubForm.setPaymentMethod")}
            />
          </FormGroup>
        </div>
        <div className="col-sm-6 col-xl-12 px-2">
          {renderAccount()}
        </div>
      </div>
    </PageCard>
  );
  //#endregion render
};

export default PaymentMethodSubForm;
